// v2.19.38b — Page démo Map Explorer : claims + POI OSM cliquables.
// Affiche pins par catégorie + PoiCardSheet au clic + debounce moveend bbox refetch.

import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import PoiCardSheet, { PIN_COLORS } from "@/components/PoiCardSheet";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const ALL_CATS = ["monuments", "commerces", "logements", "services", "tourism", "officiels"];

export default function MapExplorerPage() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const debounceRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [activeCats, setActiveCats] = useState(new Set(ALL_CATS));
  const [showClaims, setShowClaims] = useState(true);
  const [stats, setStats] = useState({ claims: 0, pois: 0, cache: "—" });

  // Init map (Abidjan Plateau par défaut)
  useEffect(() => {
    if (!containerRef.current || !TOKEN) return;
    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [-4.0244, 5.3198],
      zoom: 13,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      // Source + layer pour CLAIMS
      map.addSource("claims-pins-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "claims-pins-circles",
        type: "circle",
        source: "claims-pins-src",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 6, 14, 10, 17, 14],
          "circle-color": ["case", ["==", ["get", "type"], "pro"], PIN_COLORS.pro, PIN_COLORS.perso],
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 1.5,
          "circle-stroke-opacity": 0.9,
          "circle-opacity": 0.95,
        },
      });

      // Source + layer pour OSM POIS
      map.addSource("osm-pois-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "osm-pois-circles",
        type: "circle",
        source: "osm-pois-src",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 11, 4, 14, 7, 17, 11],
          "circle-color": [
            "match", ["get", "category_aya"],
            "monuments", PIN_COLORS.monuments,
            "commerces", PIN_COLORS.commerces,
            "officiels", PIN_COLORS.officiels,
            "tourism", PIN_COLORS.tourism,
            "logements", PIN_COLORS.logements,
            "services", PIN_COLORS.services,
            PIN_COLORS.autre,
          ],
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 1,
          "circle-stroke-opacity": 0.6,
          "circle-opacity": 0.88,
        },
      });

      // Click handlers
      const clickFeature = (e) => {
        const f = e.features?.[0];
        if (f) setSelectedFeature({ geometry: f.geometry, properties: f.properties });
      };
      map.on("click", "claims-pins-circles", clickFeature);
      map.on("click", "osm-pois-circles", clickFeature);
      map.on("mouseenter", "claims-pins-circles", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "claims-pins-circles", () => { map.getCanvas().style.cursor = ""; });
      map.on("mouseenter", "osm-pois-circles", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "osm-pois-circles", () => { map.getCanvas().style.cursor = ""; });

      // Debounced moveend → refetch
      const refetch = () => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchPins(map), 600);
      };
      map.on("moveend", refetch);
      setReady(true);
      fetchPins(map);
    });

    return () => { clearTimeout(debounceRef.current); map.remove(); };
  }, []);

  // Refetch quand filtres changent
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    fetchPins(mapRef.current);
  }, [activeCats, showClaims, ready]);

  async function fetchPins(map) {
    const b = map.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map((x) => x.toFixed(4)).join(",");

    // 1. Claims (toujours essayés si toggle on)
    if (showClaims) {
      try {
        const r = await fetch(`${API}/api/v3/map/claims-pins?bbox=${bbox}`);
        const j = await r.json();
        if (j?.type === "FeatureCollection") {
          map.getSource("claims-pins-src")?.setData(j);
          setStats((s) => ({ ...s, claims: j.count || 0 }));
        }
      } catch (_) {}
    } else {
      map.getSource("claims-pins-src")?.setData({ type: "FeatureCollection", features: [] });
      setStats((s) => ({ ...s, claims: 0 }));
    }

    // 2. POI OSM (si au moins une catégorie active)
    const cats = [...activeCats];
    if (cats.length === 0) {
      map.getSource("osm-pois-src")?.setData({ type: "FeatureCollection", features: [] });
      setStats((s) => ({ ...s, pois: 0, cache: "—" }));
      return;
    }
    try {
      const r = await fetch(`${API}/api/v3/map/osm-pois?bbox=${bbox}&categories=${cats.join(",")}`);
      const cacheHeader = r.headers.get("x-cache") || "—";
      const j = await r.json();
      if (j?.type === "FeatureCollection") {
        map.getSource("osm-pois-src")?.setData(j);
        setStats((s) => ({ ...s, pois: j.count || 0, cache: cacheHeader }));
      }
    } catch (_) {}
  }

  const toggleCat = (c) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0F1A2E" }}>
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />

      {/* Header */}
      <div style={{
        position: "absolute", top: 12, left: 12,
        background: "rgba(15,26,46,0.85)", padding: "10px 14px", borderRadius: 12,
        border: "1px solid rgba(212,175,55,0.25)", color: "#F4F1E8",
        fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
      }}>
        🗺 AYA Map Explorer
        <span style={{ marginLeft: 10, fontSize: 10, color: "rgba(244,241,232,0.5)", fontWeight: 400 }}>
          {stats.claims} claims · {stats.pois} POI OSM · cache {stats.cache}
        </span>
      </div>

      {/* Légende + filtres */}
      <div data-testid="map-filters" style={{
        position: "absolute", bottom: 16, left: 12, right: 12,
        background: "rgba(15,26,46,0.92)", padding: "12px 14px", borderRadius: 14,
        border: "1px solid rgba(212,175,55,0.2)",
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
        color: "#F4F1E8", fontSize: 11,
      }}>
        <FilterChip
          testid="filter-claims"
          active={showClaims}
          onClick={() => setShowClaims((v) => !v)}
          color={PIN_COLORS.perso}
          label="🏠 Claims"
        />
        <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
        {ALL_CATS.map((c) => (
          <FilterChip
            key={c}
            testid={`filter-${c}`}
            active={activeCats.has(c)}
            onClick={() => toggleCat(c)}
            color={PIN_COLORS[c]}
            label={c}
          />
        ))}
      </div>

      {/* Sheet POI */}
      {selectedFeature && (
        <PoiCardSheet
          feature={selectedFeature}
          onClose={() => setSelectedFeature(null)}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, color, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        background: active ? color + "22" : "transparent",
        color: active ? color : "rgba(244,241,232,0.5)",
        border: `1px solid ${active ? color + "88" : "rgba(255,255,255,0.1)"}`,
        padding: "5px 11px", borderRadius: 999, cursor: "pointer",
        fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: "capitalize",
        transition: "all 0.18s",
      }}
    >
      {label}
    </button>
  );
}
